import {
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Store } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { PROMPT_ERROR } from "@store/exception/error.action";
import { SET_CONCURRENT_LOGIN_ERROR } from "@store/login/login.action";
import { NAVIGATE_LOGIN_PAGE } from "@store/user/user.action";
import { Observable, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { isManagedBackendUrl } from "../backend-request-url.util";
import { ModalService } from "../modal.service";

// Marks the single retry attempt that happens after a successful refresh so
// the interceptor does not re-enter the refresh flow indefinitely.
const RETRY_HEADER = "X-SAT-Auth-Retry";
// Temporary allowlist for summary-shell bootstrap endpoints whose backend auth
// integration is still incomplete in DEV. These requests may fail with 401/403
// immediately after a valid login, but that should not be treated as a global
// session-expiry signal for the whole app.
const TOLERATED_SUMMARY_BOOTSTRAP_PATHS = [
  "/cn/summary",
  "/common/unified/lov",
  "/code-master/unified/lov",
  "/user/notification/list",
  "/notification/list",
] as const;

interface InterceptorErrorPayload {
  error?: string;
  errorCode?: string;
  info?: string;
  isForcedLogout?: boolean | string;
  localizedMessage?: string;
  message?: string;
  redirectUrl?: string;
}

interface InterceptorErrorLike {
  error?: InterceptorErrorPayload;
  status?: number | string;
}

interface ForcedLogoutConcurrentPayload {
  info: string;
  message: string;
}

@Injectable()
/**
 * Central HTTP auth/error policy for SAT backend requests.
 *
 * Scope:
 * - Applies to requests targeting the configured BFF base URL.
 * - Also applies to the mock backend base URL when mock routing is enabled.
 *
 * Normal flow:
 * 1. A protected non-auth API returns 401.
 * 2. The interceptor calls `/auth/refresh`.
 * 3. If refresh succeeds, the original request is retried once.
 * 4. If the retry still fails with an auth error, FE clears session state and
 *    navigates back to login.
 *
 * Login flow special case:
 * - Login failures are converted into a synthetic `HttpResponse` carrying the
 *   normalized error body and numeric status code.
 * - This lets the user store handle login failures in its regular success path
 *   instead of splitting logic across `next` and `error` callbacks.
 *
 * Forced logout special case:
 * - Concurrent-login invalidation is surfaced by the backend as an auth error
 *   with `isForcedLogout: true`.
 * - FE must preserve the backend `info`/`message` copy for the login screen
 *   instead of following the generic redirect-only path.
 *
 * Temporary DEV rollout behavior:
 * Some summary bootstrap APIs are still being integrated by a separate backend
 * team. While that work is incomplete, selected endpoints are allowed to fail
 * without forcing a global logout when
 * `environment.tolerateIncompleteSummaryBootstrapAuthFailures` is enabled.
 *
 * This exception must stay narrow and temporary. Do not expand it into a
 * general-purpose "ignore auth failures" mechanism.
 */
export class RequestInterceptorService implements HttpInterceptor {
  private readonly legacyRedirectStatuses = [
    401,
    403,
    302,
    "401",
    "403",
    "302",
  ];
  private readonly dialogErrorStatuses = [400, 500, 503, "400", "500", "503"];

  constructor(private readonly injector: Injector) {}

  private get modalService(): ModalService {
    return this.injector.get(ModalService);
  }

  private get authSessionFacade(): AuthSessionFacade {
    return this.injector.get(AuthSessionFacade);
  }

  private get store(): Store {
    return this.injector.get(Store);
  }

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    const authReq = this.withJsonContentType(request);

    return next
      .handle(authReq)
      .pipe(catchError((err) => this.handleError(err, authReq, next)));
  }

  private handleError(
    err: unknown,
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (this.shouldAttemptRefresh(request, err)) {
      return this.authSessionFacade.refreshSession(true).pipe(
        switchMap((success) => {
          if (success) {
            // Re-run the original request exactly once after refresh. Any auth
            // failure from the retry is handled by the same non-refresh policy
            // path below, including the temporary summary bootstrap soft-fail.
            const retriedRequest = request.clone({
              headers: request.headers.set(RETRY_HEADER, "1"),
            });
            return next
              .handle(retriedRequest)
              .pipe(
                catchError((retryErr) =>
                  this.handleNonRefreshError(retryErr, retriedRequest),
                ),
              );
          }

          return this.handleSessionExpiry(err, request);
        }),
        catchError(() => this.handleSessionExpiry(err, request)),
      );
    }

    return this.handleNonRefreshError(err, request);
  }

  private handleNonRefreshError(
    err: unknown,
    request: HttpRequest<any>,
  ): Observable<any> {
    const normalizedError = this.asInterceptorError(err);

    if (this.isLoginRequest(request)) {
      return this.toLoginErrorResponse(err);
    }

    if (this.isForcedLogout(err)) {
      return this.handleForcedLogout(err);
    }

    if (
      this.legacyRedirectStatuses.includes(normalizedError.status ?? "") &&
      typeof normalizedError.error?.redirectUrl === "string" &&
      normalizedError.error.redirectUrl
    ) {
      window.location.assign(normalizedError.error.redirectUrl);
      return of(err);
    }

    if (this.shouldTolerateBootstrapAuthFailure(request, err)) {
      console.warn(
        `[RequestInterceptor] Soft-failing auth error for in-progress summary bootstrap endpoint: ${request.url}`,
        err,
      );
      return of(err);
    }

    if (this.shouldNavigateToLogin(request, err)) {
      return this.handleSessionExpiry(err, request);
    }

    if (
      this.dialogErrorStatuses.includes(normalizedError.status ?? "") &&
      !this.isLoginRequest(request) &&
      !this.isPolicySearchRequest(request) &&
      !this.isAdminAlertSaveRequest(request)
    ) {
      console.warn(err);
      const error =
        normalizedError.error.error ?? normalizedError.error?.errorCode;

      this.store.dispatch(new PROMPT_ERROR(error));
    }

    return of(err);
  }

  private toLoginErrorResponse(err: unknown): Observable<HttpResponse<any>> {
    const normalizedError = this.asInterceptorError(err);
    const status = this.toNumericStatus(normalizedError.status);
    const errorBody = normalizedError.error;

    // Preserve the backend payload shape where possible, but always surface a
    // numeric status so the store can normalize success and failure responses
    // through the same adapter.
    const body =
      errorBody && typeof errorBody === "object" && !Array.isArray(errorBody)
        ? {
            status,
            ...errorBody,
          }
        : {
            status,
            error: errorBody,
          };

    return of(
      new HttpResponse({
        status,
        body,
      }),
    );
  }

  private toNumericStatus(status: unknown): number {
    if (typeof status === "number" && Number.isFinite(status)) {
      return status;
    }

    const parsed = Number(status);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 500;
  }

  private handleSessionExpiry(
    err: unknown,
    request: HttpRequest<any>,
  ): Observable<any> {
    if (!this.isLoginRequest(request)) {
      if (this.isForcedLogout(err)) {
        return this.handleForcedLogout(err);
      }

      this.authSessionFacade.clearSession();
      this.store.dispatch(new NAVIGATE_LOGIN_PAGE());
    }

    return of(err);
  }

  private handleForcedLogout(err: unknown): Observable<any> {
    const normalizedError = this.asInterceptorError(err);
    this.authSessionFacade.clearSession();
    this.store.dispatch([
      new SET_CONCURRENT_LOGIN_ERROR(
        this.toForcedLogoutConcurrentPayload(normalizedError.error),
      ),
      new NAVIGATE_LOGIN_PAGE(),
    ]);
    return of(err);
  }

  private shouldAttemptRefresh(
    request: HttpRequest<any>,
    err: unknown,
  ): boolean {
    const normalizedError = this.asInterceptorError(err);

    if (!this.isBffRequest(request) || this.isAuthEndpoint(request)) {
      return false;
    }

    if (request.headers.has(RETRY_HEADER)) {
      return false;
    }

    if (this.isForcedLogout(normalizedError)) {
      return false;
    }

    if (normalizedError.status !== 401) {
      return false;
    }

    if (typeof normalizedError.error?.redirectUrl === "string") {
      return false;
    }

    return this.authSessionFacade.hasSession();
  }

  private shouldNavigateToLogin(
    request: HttpRequest<any>,
    err: unknown,
  ): boolean {
    const normalizedError = this.asInterceptorError(err);

    if (!this.isBffRequest(request) || this.isLoginRequest(request)) {
      return false;
    }

    if (![401, 403, "401", "403"].includes(normalizedError.status ?? "")) {
      return false;
    }

    return (
      typeof normalizedError.error?.errorCode === "string" ||
      request.headers.has(RETRY_HEADER)
    );
  }

  private shouldTolerateBootstrapAuthFailure(
    request: HttpRequest<any>,
    err: unknown,
  ): boolean {
    const normalizedError = this.asInterceptorError(err);

    // Explicit feature flag so UAT/PROD keep strict auth handling even while
    // DEV is operating against a partially completed summary backend.
    if (!environment.tolerateIncompleteSummaryBootstrapAuthFailures) {
      return false;
    }

    if (!this.isBffRequest(request) || this.isLoginRequest(request)) {
      return false;
    }

    if (![401, 403, "401", "403"].includes(normalizedError.status ?? "")) {
      return false;
    }

    // Only allow known summary-shell bootstrap endpoints to degrade. Business
    // actions outside this list must continue to behave as real auth failures.
    return TOLERATED_SUMMARY_BOOTSTRAP_PATHS.some((path) =>
      request.url.includes(path),
    );
  }

  private isForcedLogout(err: unknown): boolean {
    const normalizedError = this.asInterceptorError(err);

    return (
      this.legacyRedirectStatuses.includes(normalizedError.status ?? "") &&
      (normalizedError.error?.isForcedLogout === true ||
        normalizedError.error?.isForcedLogout === "true")
    );
  }

  private toForcedLogoutConcurrentPayload(
    errorBody?: InterceptorErrorPayload,
  ): ForcedLogoutConcurrentPayload {
    const info =
      typeof errorBody?.info === "string" && errorBody.info.trim().length > 0
        ? errorBody.info
        : "Concurrent login detected on this account";
    const message = this.resolveErrorMessage(errorBody);

    return {
      info,
      message,
    };
  }

  private asInterceptorError(err: unknown): InterceptorErrorLike {
    if (!err || typeof err !== "object" || Array.isArray(err)) {
      return {};
    }

    return err as InterceptorErrorLike;
  }

  private resolveErrorMessage(errorBody?: InterceptorErrorPayload): string {
    if (
      typeof errorBody?.localizedMessage === "string" &&
      errorBody.localizedMessage.trim().length > 0
    ) {
      return errorBody.localizedMessage;
    }
    if (
      typeof errorBody?.message === "string" &&
      errorBody.message.trim().length > 0
    ) {
      return errorBody.message;
    }
    return "For security reasons, you have been logged out of your current session. Please re-login to continue or contact IT Helpdesk for assistance.";
  }

  private withJsonContentType(request: HttpRequest<any>): HttpRequest<any> {
    if (
      request.headers.has("Content-Type") ||
      request.body instanceof FormData ||
      request.method === "GET"
    ) {
      return request;
    }

    return request.clone({
      headers: request.headers.set("Content-Type", "application/json"),
    });
  }

  private isBffRequest(request: HttpRequest<any>): boolean {
    return isManagedBackendUrl(request.url);
  }

  private isAuthEndpoint(request: HttpRequest<any>): boolean {
    return (
      this.isLoginRequest(request) ||
      request.url.endsWith("/auth/refresh") ||
      request.url.endsWith("/auth/logout")
    );
  }

  private isLoginRequest(request: HttpRequest<any>): boolean {
    return request.url.endsWith("/auth/login");
  }

  private isPolicySearchRequest(request: HttpRequest<any>): boolean {
    return request.url.includes("/policy/search");
  }

  private isAdminAlertSaveRequest(request: HttpRequest<any>): boolean {
    return request.url.includes("/admin/alert/save");
  }
}
